import {
  cacheKeys,
  FeatureName,
  Media,
  MediaPickerComponent,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentLocation,
  useCurrentWorkspace,
  useCustomMutation,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useLocalSearchParams } from 'expo-router';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View, ViewStyle } from 'react-native';
import { Text } from 'react-native-paper';
import { array, string, object as yupObject } from 'yup';
import { DividerText } from '../../screens/login/dividerText';
import { Layout, Spacing } from '../../styles';
import { Colors } from '../../themes';
import { ControlledTextInput, SaveFormBottom } from '../forms';
import { GroupSelector } from '../group';
import { KeyboardSafeView } from '../keyboard/keyboardSafeView';
import { HorizontalMediaGallery, SingleImageInput } from '../media';
import { BottomComponent, LeftButton, SaveButton } from '../navigation';

export type NewAssetStyles = {
  container: ViewStyle;
  contentContainer: ViewStyle;
  imageGalleryContainer?: ViewStyle;
};

export type NewAssetProps = {
  onCancel: () => void;
  mediaPicker: MediaPickerComponent;
  topComponentBar: any;
  styles: StyleSheet.NamedStyles<NewAssetStyles>;
  onSaved: (assetId: string) => void;
  bottomBar?: BottomComponent;
  onSelectorEdit?: () => void;
};

export const NewAsset = (props: NewAssetProps) => {
  const MediaPicker = props.mediaPicker;
  const TopComponentBar = props.topComponentBar;

  const { t } = useTranslation();
  const params = useLocalSearchParams();
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  const { tagId, tagCode } = params;
  const [coverImageId, setCoverImageId] = useState<string | undefined>();
  const [uploading, setUploading] = useState(false);
  const { mutate, isPending: isLoading } = useCustomMutation({
    query: createAsset,
  });
  const { createGroup } = useCreateGroup();
  const [assetMedia, setAssetMedia] = useState<string[]>([]);
  const workspace = useCurrentWorkspace();
  const { getLocation } = useCurrentLocation();
  const queryClient = useQueryClient();

  const { groups } = useAllWorkspaceGroups(workspace?.id);
  const [selectedGroups, setSelectedGroups] = useState<
    Array<{ id: string; name: string }>
  >([]);

  const addMedia = (newMediaItem: Media) => {
    if (!newMediaItem) return;
    setAssetMedia((prevState) => [...prevState, newMediaItem.id]);
  };

  const deleteMedia = (mediaId: string) => {
    setAssetMedia((prevState) => prevState.filter((id) => id !== mediaId));
  };

  const formSchema = yupObject({
    name: string().required(),
    description: string().optional(),
    details: string().optional(),
    groupIds: array().of(string().required()).default([]),
    tagCode: string().optional(),
  });

  type NewAssetFormValues = {
    name: string;
    description?: string;
    details?: string;
    groupIds: string[];
    tagCode?: string;
  };

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useForm<NewAssetFormValues>({
    resolver: yupResolver<NewAssetFormValues>(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      description: '',
      details: '',
      groupIds: [],
      tagCode: tagCode ? String(tagCode) : undefined,
    },
  });

  const canSave = useMemo(() => {
    return isDirty && isValid && !uploading && !isLoading;
  }, [isValid, uploading, isLoading, isDirty]);

  const setCoverImage = (image: Media) => {
    setCoverImageId(image.id);
  };

  const onSubmit = async (data: any) => {
    let location;
    if (tagId) {
      location = await getLocation();
    }
    delete data.tagCode;
    const input: any = {
      ...data,
      groupIds: selectedGroups.map((group) => group.id),
      ...(tagId && { tagId }),
      ...(location && { lastLocation: location }),
      workspaceId: workspace.id,
      mediaIds: assetMedia.length > 0 ? assetMedia : undefined,
      coverImageId: coverImageId,
    };
    mutate(
      { input: input },
      {
        onSettled: (response: any) => {
          const assetId = response?.asset?.addAsset?.id;
          if (assetId) {
            queryClient.invalidateQueries({ queryKey: cacheKeys('asset').all });
            props.onSaved(assetId);
          }
        },
      }
    );
  };

  const cancelButton = (
    <LeftButton
      label={t('topNav.cancel')}
      hideCaret={true}
      onPress={() => props.onCancel()}
      style={[styles.baseButton, styles.cancelButton]}
    />
  );
  const addButton = (
    <SaveButton
      onPress={handleSubmit(onSubmit)}
      label={t('topNav.add')}
      disabled={!canSave}
      isLoading={isLoading}
      activeTextColor={Colors.white}
      style={[
        styles.baseButton,
        canSave ? styles.addButtonEnabled : styles.addButtonDisabled,
      ]}
    />
  );

  const formContent = (
    <ScrollView
      showsHorizontalScrollIndicator={false}
      scrollEnabled
      showsVerticalScrollIndicator={false}
      style={props.styles.contentContainer}
    >
      <View style={styles.outerContainer}>
        <View style={styles.baseContainer}>
          <View>
            <Text variant={'titleMedium'} style={styles.formItemTitle}>
              1. {t('asset.assetPicture')}:
            </Text>
          </View>
          <View style={styles.coverImage}>
            <SingleImageInput
              onImageAdded={setCoverImage}
              onUploadingChanged={setUploading}
              imagePicker={MediaPicker}
              allowsEditing={true}
              displayWidth={100}
            />
          </View>
        </View>
        <View style={[styles.baseContainer, styles.formItem]}>
          <View>
            <Text variant={'titleMedium'} style={styles.formItemTitle}>
              2. {t('asset.assetName')}:
            </Text>
          </View>
          <ControlledTextInput
            testID="add-asset-name"
            control={control}
            placeholder={t('asset.namePlaceholder')}
            name="name"
            outlinedTextInput
            disableInnerStyle
          />
        </View>

        <DividerText
          text={t('asset.optionalSetting')}
          styleContainer={styles.dividerContainer}
        />

        <View style={styles.baseContainer}>
          <View>
            <Text variant={'titleMedium'} style={styles.formItemTitle}>
              {`3. ${t('asset.newAdditionalMedia')}`}
            </Text>
          </View>
          <View>
            <HorizontalMediaGallery
              onMediaAdded={addMedia}
              onMediaDeleted={deleteMedia}
              mediaPicker={MediaPicker as MediaPickerComponent}
              onUploadingChanged={setUploading}
              canEditMedia={true}
            />
          </View>
        </View>
        <View style={[styles.baseContainer, styles.formItem]}>
          <View>
            <Text variant={'titleMedium'} style={styles.formItemTitle}>
              {`4. ${t('asset.secondaryDescription')}`}
            </Text>
          </View>
          <ControlledTextInput
            control={control}
            placeholder={t('asset.descriptionPlaceholder')}
            name="description"
            outlinedTextInput
            disableInnerStyle
          />
        </View>
        <View style={[styles.baseContainer, styles.formItem]}>
          <View>
            <Text variant={'titleMedium'} style={styles.formItemTitle}>
              {`5. ${t('asset.details')}`}
            </Text>
          </View>
          <ControlledTextInput
            control={control}
            placeholder={t('asset.detailsPlaceHolder')}
            name="details"
            textArea={true}
            outlinedTextInput
            disableInnerStyle={false}
          />
        </View>
        <View
          style={[styles.baseContainer, styles.formItem, styles.lastFormItem]}
        >
          <View>
            <Text variant={'titleMedium'} style={[styles.formItemTitle]}>
              {`6. ${t('asset.groups')}`}
            </Text>
          </View>
          <GroupSelector
            selectedGroups={selectedGroups}
            availableGroups={groups}
            onGroupsChange={setSelectedGroups}
            onCreateGroup={createGroup}
            disabled={false}
            useCropCircle={isCropCircleRedesignEnabled}
            onSelectorEdit={props.onSelectorEdit}
          />
        </View>
        {tagCode && (
          <View
            style={[
              styles.baseContainer,
              styles.formItem,
              styles.formItemBottom,
            ]}
          >
            <View>
              <Text variant={'titleMedium'} style={styles.formItemTitle}>
                {`7. ${t('asset.tagCode')}`}
              </Text>
            </View>
            <ControlledTextInput
              control={control}
              name="tagCode"
              outlinedTextInput
              editable={false}
              disableInnerStyle={true}
              textColor={'#8E8E93'}
              backgroundColor={'#E9E9EF'}
            />
          </View>
        )}
      </View>
    </ScrollView>
  );

  if (props.bottomBar) {
    return (
      <KeyboardSafeView style={[props.styles.container]}>
        <SaveFormBottom
          isDirty={isDirty && !isLoading}
          isValid={canSave}
          onSubmit={async () => {
            await handleSubmit(onSubmit)();
          }}
          bottomBar={props.bottomBar}
          onCancel={props.onCancel}
        >
          {formContent}
        </SaveFormBottom>
      </KeyboardSafeView>
    );
  }

  return (
    <>
      <TopComponentBar
        title={t('asset.newTitle')}
        left={cancelButton}
        right={addButton}
      />
      <KeyboardSafeView style={[props.styles.container]}>
        {formContent}
      </KeyboardSafeView>
    </>
  );
};

const createAsset = `
  mutation Asset($input: AddAssetInput!) {
    asset {
      addAsset(input: $input) {
        id
      }
    }
  }
`;

const styles = StyleSheet.create({
  outerContainer: {
    paddingBottom: 200,
  },
  baseContainer: {
    backgroundColor: Colors.white,
    paddingVertical: Spacing.basePadding.paddingVertical,
    paddingHorizontal: Spacing.basePadding.base,
    flexDirection: 'column',
    width: '100%',
  },
  coverImage: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  baseButton: {
    width: 64,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    borderRadius: 10,
    marginHorizontal: Spacing.baseMargin.base,
  },
  cancelButton: {
    marginLeft: 8,
    marginRight: Spacing.baseMargin.marginHorizontal * 1.3,
  },
  addButtonDisabled: {
    backgroundColor: Colors.grayDivider,
  },
  addButtonEnabled: {
    backgroundColor: Colors.primary,
  },
  container: {
    ...Layout.container,
    backgroundColor: Colors.primaryGrayBackground,
  },
  contentContainer: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingVertical: Spacing.basePadding.paddingVertical,
  },
  formItem: {
    width: '100%',
    marginTop: Spacing.baseMargin.marginVertical,
  },
  lastFormItem: {
    marginBottom: 20,
  },
  formItemBottom: {
    marginBottom: 40,
  },
  formItemTitle: {
    fontSize: 16,
    lineHeight: 16,
    color: Colors.black,
    fontWeight: '700',
    marginBottom: Spacing.baseMargin.marginVertical,
  },
  dividerContainer: {
    paddingHorizontal: 0,
    marginTop: 35,
    marginBottom: 35,
    maxWidth: '100%',
  },
  addNewButton: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: 32,
    paddingVertical: 0,
  },
  buttonLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.primaryDark,
  },
});
